import { Button } from '@ipfabric/ui-components/Button/Button';
import type {
  ButtonRenderedAsButtonProps,
  ButtonSize,
} from '@ipfabric/ui-components/Button/Button.types';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import type { IconGlyph } from '@ipfabric/ui-components/Icon/IconGlyph';
import type { TableActionsItem } from 'components/Table/model/TableModel';
import type { RefObject } from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';

// Threshold width in pixels: if screen is narrower than this, show button in menu
const DEFAULT_THRESHOLD = 765;

interface UseResponsiveTableButtonProps
  extends Partial<ButtonRenderedAsButtonProps> {
  /**
   * Label text for the button
   */
  label: string;
  /**
   * Icon glyph name (e.g., 'fa-arrows-rotate')
   */
  icon: IconGlyph;
  /**
   * Click handler
   */
  onClick: () => void;
  /**
   * Ref to the container element for width tracking
   */
  containerRef: RefObject<HTMLElement | null>;
  /**
   * Optional threshold width in pixels. Defaults to 765px
   */
  threshold?: number;
  /**
   * Whether the button should be shown. If false, returns null
   */
  show?: boolean;
  /**
   * Size of the button when shown standalone. Defaults to 'default'
   */
  size?: ButtonSize;
}

interface UseResponsiveTableButtonReturn {
  /**
   * The table action item - either standalone button (wide) or menu item (narrow)
   */
  actionItem: TableActionsItem | null;
}

/**
 * Hook that creates a responsive table button that automatically moves to
 * the context menu when the screen is too narrow.
 * Uses ResizeObserver to track the container width for accurate responsive behavior.
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { actionItem } = useResponsiveTableButton({
 *   label: 'Override with global settings',
 *   icon: 'fa-arrows-rotate',
 *   onClick: handleUseGlobalSettings,
 *   containerRef,
 *   show: showUpdateFromGlobalSettingsButton,
 *   threshold: 765, // optional, defaults to 765px
 * });
 *
 * return (
 *   <div ref={containerRef}>
 *     <Table
 *       menuItems={[
 *         { standalone: true, elementWithKey: <AddButton /> },
 *         actionItem,
 *       ]}
 *     />
 *   </div>
 * );
 * ```
 */
export const useResponsiveTableButton = (
  props: UseResponsiveTableButtonProps,
): UseResponsiveTableButtonReturn => {
  const {
    label,
    icon,
    onClick,
    containerRef,
    threshold = DEFAULT_THRESHOLD,
    show = true,
    size = 'default',
    ...buttonProps
  } = props;

  const [isWideScreen, setIsWideScreen] = useState(true);
  const [element, setElement] = useState<HTMLElement | null>(null);

  // Track when the ref element becomes available
  useLayoutEffect(() => {
    setElement(containerRef.current);
  });

  useEffect(() => {
    if (!element) {
      return;
    }

    setIsWideScreen(element.offsetWidth > threshold);

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setIsWideScreen(width > threshold);
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [element, threshold]);

  if (!show) {
    return { actionItem: null };
  }

  const actionItem: TableActionsItem = isWideScreen
    ? {
        standalone: true,
        elementWithKey: (
          <Button
            {...buttonProps}
            key={`responsive-button-${label}`}
            icon={icon}
            onClick={onClick}
            type='button'
            variant={buttonProps.variant || 'outline'}
            size={size}
            className={buttonProps.className}
          >
            {label}
          </Button>
        ),
      }
    : {
        label,
        startAdornment: <Icon glyph={icon} iconStyle={buttonProps.iconStyle} />,
        onClick,
      };

  return { actionItem };
};
